import {
  AuthPartner,
  AuthPartnerFilter,
  AuthPartnerInput,
  AuthPartnerUpdate,
} from "@happy-legal/graphql-types";
import { graphQlClient } from "../clients";
import {
  MUTATION_ADD_AUTH_PARTNER,
  MUTATION_DELETE_AUTH_PARTNER,
  MUTATION_RECREATE_AUTH_PARTNER_KEY_PAIR,
  MUTATION_UPDATE_AUTH_PARTNER,
} from "../gql/mutations";
import {
  QUERY_GET_ALL_AUTH_PARTNERS,
  QUERY_GET_AUTH_PARTNER,
} from "../gql/queries";
import { authPartnerInitState } from "../providers/AuthPartnerProvider";
import { handleCatchReducer } from "../reducer/handleCatchReducer";

export const GET_ALL_AUTH_PARTNERS = "GET_ALL_AUTH_PARTNERS";
type TGET_ALL_AUTH_PARTNERS = {
  type: "GET_ALL_AUTH_PARTNERS";
  limit?: number;
  offset?: number;
  filter?: AuthPartnerFilter;
  notEqual?: AuthPartnerFilter;
};

export const GET_AUTH_PARTNER = "GET_AUTH_PARTNER";
type TGET_AUTH_PARTNER = {
  type: "GET_AUTH_PARTNER";
  id: string;
};

export const ADD_AUTH_PARTNER = "ADD_AUTH_PARTNER";
type TADD_AUTH_PARTNER = {
  type: "ADD_AUTH_PARTNER";
  payload: AuthPartnerInput;
};

export const RECREATE_AUTH_PARTNER_KEY_PAIR = "RECREATE_AUTH_PARTNER_KEY_PAIR";
type TRECREATE_AUTH_PARTNER_KEY_PAIR = {
  type: "RECREATE_AUTH_PARTNER_KEY_PAIR";
  id: string;
};

export const UPDATE_AUTH_PARTNER = "UPDATE_AUTH_PARTNER";
type TUPDATE_AUTH_PARTNER = {
  type: "UPDATE_AUTH_PARTNER";
  id: string;
  payload: AuthPartnerUpdate;
};

export const DELETE_AUTH_PARTNER = "DELETE_AUTH_PARTNER";
type TDELETE_AUTH_PARTNER = {
  type: "DELETE_AUTH_PARTNER";
  id: string;
};

export type TAuthPartnerAction =
  | TGET_ALL_AUTH_PARTNERS
  | TGET_AUTH_PARTNER
  | TADD_AUTH_PARTNER
  | TRECREATE_AUTH_PARTNER_KEY_PAIR
  | TUPDATE_AUTH_PARTNER
  | TDELETE_AUTH_PARTNER;

export type TAuthPartnerWithSecretKey = { secretKey?: string } & AuthPartner;

export type TAuthParterState = {
  authPartner: TAuthPartnerWithSecretKey;
  allAuthPartners: {
    items: TAuthPartnerWithSecretKey[];
    total: number;
  };
};

export const authPartnerReducer = async (
  state: TAuthParterState,
  action: TAuthPartnerAction
) => {
  // allAuthPartners were previously loaded
  const hasAllAuthPartners = !!state?.allAuthPartners?.items.length;

  try {
    switch (action.type) {
      case GET_ALL_AUTH_PARTNERS: {
        const {
          type, // eslint-disable-line @typescript-eslint/no-unused-vars
          ...variables
        } = action;

        const newAllAuthPartnersState = await graphQlClient(
          QUERY_GET_ALL_AUTH_PARTNERS,
          variables
        );

        const newState: TAuthParterState = {
          ...state,
          allAuthPartners: newAllAuthPartnersState,
        };

        return newState;
      }

      case GET_AUTH_PARTNER: {
        const newAuthPartnerState = await graphQlClient(
          QUERY_GET_AUTH_PARTNER,
          {
            id: action.id,
          }
        );

        return { ...state, authPartner: newAuthPartnerState };
      }

      case ADD_AUTH_PARTNER: {
        const { secretKey, authPartner } = await graphQlClient(
          MUTATION_ADD_AUTH_PARTNER,
          {
            input: action.payload,
          }
        );

        const newAuthPartnerState = { ...authPartner, secretKey };

        const newState: TAuthParterState = {
          ...state,
          authPartner: newAuthPartnerState,
        };

        if (hasAllAuthPartners) {
          newState.allAuthPartners.items = [
            ...state.allAuthPartners.items,
            newAuthPartnerState.items,
          ];

          newState.allAuthPartners.total += 1;
        }

        return newState;
      }

      case RECREATE_AUTH_PARTNER_KEY_PAIR: {
        const { secretKey, authPartner } = await graphQlClient(
          MUTATION_RECREATE_AUTH_PARTNER_KEY_PAIR,
          { id: action.id }
        );

        const newAuthPartnerState = { ...authPartner, secretKey };

        const newState: TAuthParterState = {
          ...state,
          authPartner: newAuthPartnerState,
        };

        if (hasAllAuthPartners) {
          const newAllAuthPartnersItemsState = state.allAuthPartners.items.map(
            (authPartner) =>
              authPartner.id === action.id ? newAuthPartnerState : authPartner
          );

          newState.allAuthPartners.items = newAllAuthPartnersItemsState;
        }

        return newState;
      }

      case UPDATE_AUTH_PARTNER: {
        const newAuthPartnerState = await graphQlClient(
          MUTATION_UPDATE_AUTH_PARTNER,
          { id: action.id, input: action.payload }
        );

        const newState: TAuthParterState = {
          ...state,
          authPartner: newAuthPartnerState,
        };

        if (hasAllAuthPartners) {
          const newAllAuthPartnersItemsState = state.allAuthPartners.items.map(
            (authPartner) =>
              authPartner.id === action.id ? newAuthPartnerState : authPartner
          );

          newState.allAuthPartners.items = newAllAuthPartnersItemsState;
        }

        return newState;
      }

      case DELETE_AUTH_PARTNER: {
        const isDeleted = await graphQlClient(MUTATION_DELETE_AUTH_PARTNER, {
          id: action.id,
        });

        const newState: TAuthParterState = {
          ...state,
          authPartner: authPartnerInitState.authPartner,
        };

        if (hasAllAuthPartners) {
          const newAllAuthPartnersItemsState =
            state.allAuthPartners.items.filter(
              (authPartner) => authPartner.id !== action.id
            );

          newState.allAuthPartners.items = newAllAuthPartnersItemsState;

          newState.allAuthPartners.total -= 1;
        }

        return isDeleted ? newState : state;
      }

      default: {
        return state;
      }
    }
  } catch (err) {
    handleCatchReducer("authPartner", err);
    return authPartnerInitState;
  }
};
